import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MdCheck, MdClose, MdExpandMore, MdSearch } from "react-icons/md";
import { UI_DROPDOWN_MENU, UI_SELECT } from "lib/uiClasses";

export type MultiSelectOption = {
  value: number | string;
  label: string;
  description?: string;
};

export type MultiSelectProps = {
  value: Array<number | string>;
  options: MultiSelectOption[];
  onChange: (next: Array<number | string>) => void;
  placeholder?: string;
  /** Optional controlled search — set if caller wants remote filtering. */
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  /** Max number of chips rendered before "+N more". */
  maxChips?: number;
  isArabic?: boolean;
  /** Portal dropdown to document.body (recommended inside modals). Default true. */
  portal?: boolean;
};

const DROPDOWN_Z = 9999;

/**
 * Searchable chip multi-select. Designed for light admin forms; supports
 * both local (auto-filtered by label) and remote (onSearchChange) modes.
 */
export default function MultiSelect({
  value,
  options,
  onChange,
  placeholder,
  searchTerm,
  onSearchChange,
  loading,
  disabled = false,
  className = "",
  maxChips = 5,
  isArabic = false,
  portal = true,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [localTerm, setLocalTerm] = useState("");
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const effectiveTerm = searchTerm ?? localTerm;
  const byValue = useMemo(() => {
    const map = new Map<string, MultiSelectOption>();
    for (const o of options) map.set(String(o.value), o);
    return map;
  }, [options]);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setMenuStyle(null);
    setLocalTerm("");
  }, []);

  const openMenu = useCallback(() => {
    if (disabled || loading) return;
    setOpen(true);
    requestAnimationFrame(() => {
      const trigger = triggerRef.current;
      if (!trigger || !portal) return;
      const rect = trigger.getBoundingClientRect();
      const estimatedHeight = 280;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
      const top = openUp ? rect.top - estimatedHeight - 4 : rect.bottom + 4;
      setMenuStyle({
        position: "fixed",
        top: Math.max(8, Math.min(top, window.innerHeight - estimatedHeight - 8)),
        left: Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8)),
        width: rect.width,
        zIndex: DROPDOWN_Z,
      });
    });
  }, [disabled, loading, portal]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      closeMenu();
    };
    const timer = window.setTimeout(() => document.addEventListener("mousedown", onClick), 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open, closeMenu]);
  const filteredOptions = useMemo(() => {
    if (onSearchChange) return options;
    const term = effectiveTerm.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => o.label.toLowerCase().includes(term));
  }, [options, effectiveTerm, onSearchChange]);

  const toggle = (val: number | string) => {
    const key = String(val);
    if (value.map(String).includes(key)) {
      onChange(value.filter((v) => String(v) !== key));
    } else {
      onChange([...value, val]);
    }
  };

  const selectedChips = value.slice(0, maxChips).map((v) => {
    const opt = byValue.get(String(v));
    return (
      <span
        key={String(v)}
        className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-400/10 dark:text-brand-200"
      >
        {opt?.label ?? String(v)}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle(v);
          }}
          className="text-brand-500 hover:text-brand-700 dark:text-brand-200"
          aria-label="Remove"
        >
          <MdClose className="h-3 w-3" />
        </button>
      </span>
    );
  });

  const resolvedPlaceholder =
    placeholder ?? (isArabic ? "اختر..." : "Select...");

  const dropdown = open && (!portal || menuStyle) ? (
    <div
      ref={menuRef}
      className={`overflow-hidden ${UI_DROPDOWN_MENU} ${portal ? "" : "absolute z-[9999] mt-1 w-full"}`}
      style={portal ? menuStyle ?? undefined : undefined}
    >
      <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2 dark:border-white/[0.08]">
        <MdSearch className="h-4 w-4 shrink-0 text-gray-400 dark:text-slate-500" />
        <input
          value={effectiveTerm}
          onChange={(e) => {
            const v = e.target.value;
            if (onSearchChange) onSearchChange(v);
            else setLocalTerm(v);
          }}
          placeholder={isArabic ? "ابحث..." : "Search..."}
          className="h-7 w-full border-0 bg-transparent text-sm outline-none placeholder:text-gray-400 dark:text-white dark:placeholder:text-slate-500"
        />
      </div>
      <div className="max-h-56 overflow-y-auto py-1">
        {loading ? (
          <div className="px-3 py-3 text-center text-xs text-gray-500">
            {isArabic ? "جارٍ التحميل..." : "Loading..."}
          </div>
        ) : filteredOptions.length === 0 ? (
          <div className="px-3 py-3 text-center text-xs text-gray-500">
            {isArabic ? "لا توجد اختيارات" : "No options"}
          </div>
        ) : (
          filteredOptions.map((opt) => {
            const selected = value.map(String).includes(String(opt.value));
            return (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => toggle(opt.value)}
                className={`flex w-full items-center justify-between gap-2 px-3 py-2.5 text-start text-sm hover:bg-gray-100 dark:hover:bg-white/10 ${
                  selected ? "bg-brand-50 dark:bg-brand-500/10" : ""
                }`}
              >
                <span className="min-w-0 truncate">
                  <span className="font-medium text-gray-800 dark:text-white">{opt.label}</span>
                  {opt.description ? (
                    <span className="ml-2 text-xs text-gray-500">{opt.description}</span>
                  ) : null}
                </span>
                {selected ? (
                  <MdCheck className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-300" />
                ) : (
                  <span className="h-4 w-4 shrink-0 rounded border border-gray-300 dark:border-white/20" />
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  ) : null;

  return (
    <div ref={containerRef} className={`relative ${disabled ? "opacity-60" : ""} ${className}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled || loading}
        onClick={() => (open ? closeMenu() : openMenu())}
        className={`${UI_SELECT} flex min-h-[42px] flex-wrap items-center gap-1 py-1.5 text-start shadow-sm`}
      >
        {value.length === 0 ? (
          <span className="flex-1 text-gray-400 dark:text-gray-500">{resolvedPlaceholder}</span>
        ) : (
          <>
            {selectedChips}
            {value.length > maxChips ? (
              <span className="text-xs text-gray-500">+{value.length - maxChips}</span>
            ) : null}
          </>
        )}
        <MdExpandMore
          className={`ms-auto h-5 w-5 shrink-0 text-gray-400 transition-transform dark:text-slate-500 ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>
      {!portal && dropdown}
      {portal && dropdown ? createPortal(dropdown, document.body) : null}
    </div>
  );
}
